import React, { useState, useEffect, useCallback } from 'react';

// Cyberpunk-style neon colors
const neonCyan = '#00ffff';
const neonMagenta = '#ff00ff';
const backgroundDark = '#0a0a0a';

// Full-sized resource display
export function ResourceDisplay({ resourceName, lastChange, amountAtLastChange, rate }) {
  const calculateAmount = useCallback(() => {
    const last = new Date(lastChange).getTime();
    const now = Date.now();
    return amountAtLastChange + rate * ((now - last) / 1000);
  }, [lastChange, amountAtLastChange, rate]);

  const [currentAmount, setCurrentAmount] = useState(calculateAmount);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAmount(calculateAmount());
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateAmount]);

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: backgroundDark,
    border: `2px solid ${neonCyan}`,
    boxShadow: `0 0 10px ${neonCyan}, 0 0 20px ${neonMagenta}`,
    borderRadius: '8px',
    padding: '12px',
    minWidth: '160px',
    color: neonCyan,
    fontFamily: '"Orbitron", monospace',
    textAlign: 'center',
  };

  const headerStyle = {
    margin: 0,
    marginBottom: '8px',
    fontSize: '1.4em',
    fontWeight: 'bold',
    color: neonMagenta,
    textShadow: `0 0 5px ${neonMagenta}`,
  };

  const statStyle = {
    margin: '4px 0',
    fontSize: '1.1em',
    textShadow: `0 0 3px ${neonCyan}`,
  };

  return (
    <div style={containerStyle}>
      <h3 style={headerStyle}>{resourceName}</h3>
      <p style={statStyle}>Amount: {currentAmount.toFixed(2)}</p>
      <p style={statStyle}>Rate: {rate.toFixed(2)}/s</p>
    </div>
  );
}

// Compact display for small screens, uses emoji and single line
export function ResourceDisplayCompact({ resourceEmoji, lastChange, amountAtLastChange, rate }) {
  const calculateAmount = useCallback(() => {
    const last = new Date(lastChange).getTime();
    const now = Date.now();
    return amountAtLastChange + rate * ((now - last) / 1000);
  }, [lastChange, amountAtLastChange, rate]);

  const [currentAmount, setCurrentAmount] = useState(calculateAmount);

  useEffect(() => {
    const interval = setInterval(() => setCurrentAmount(calculateAmount()), 1000);
    return () => clearInterval(interval);
  }, [calculateAmount]);

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: backgroundDark,
    border: `2px solid ${neonMagenta}`,
    boxShadow: `0 0 5px ${neonMagenta}`,
    borderRadius: '5px',
    padding: '6px 8px',
    color: neonCyan,
    fontFamily: '"Orbitron", monospace',
    fontSize: '0.9em',
  };

  return (
    <div style={containerStyle}>
      <span style={{ marginRight: '6px' }}>{resourceEmoji}</span>
      <span>{currentAmount.toFixed(2)} (+{rate.toFixed(2)}/s)</span>
    </div>
  );
}

// Demo component rendering full and compact views side by side
export default function ResourceDisplayDemo() {
  const dummyResources = [
    { resourceName: 'Gold', resourceEmoji: '🪙', lastChange: new Date(Date.now() - 5000).toISOString(), amountAtLastChange: 100, rate: 0.5 },
    { resourceName: 'Wood', resourceEmoji: '🌲', lastChange: new Date(Date.now() - 10000).toISOString(), amountAtLastChange: 50, rate: 1.2 },
    { resourceName: 'Stone', resourceEmoji: '🪨', lastChange: new Date(Date.now() - 2000).toISOString(), amountAtLastChange: 20, rate: 0.8 },
  ];

  const demoContainerStyle = {
    display: 'flex',
    gap: '32px',
    padding: '24px',
    backgroundColor: backgroundDark,
    justifyContent: 'center',
    flexWrap: 'wrap',
  };

  const resourcePairStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
  };

  return (
    <div style={demoContainerStyle}>
      {dummyResources.map((res) => (
        <div key={res.resourceName} style={resourcePairStyle}>
          <ResourceDisplay {...res} />
          <ResourceDisplayCompact {...res} />
        </div>
      ))}
    </div>
  );
}
